import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { detectMedia } from "@/lib/media";
import { Sparkles, Play, VolumeX } from "lucide-react";

/**
 * "A Little Peek" — the landing page's autoplaying, muted free-story
 * showcase, matching the sibling Little Verses app's own hero-adjacent
 * section (badge + big promise headline + autoplay video with an Unmute
 * overlay on the left, a numbered "how it works" + CTA on the right).
 * Replaces the earlier plain "Try Story #1 free" banner with the fuller
 * treatment the client asked to match. `fallbackTitle` renders instantly
 * (Landing.js already has it from STORY_SHOWCASE) so the section never
 * sits empty while GET /stories/1 (public, see FREE_PREVIEW_SEQ on the
 * backend) is still in flight.
 */
export default function FreeStoryShowcase({ fallbackTitle }) {
  const { getLoc } = useLang();
  const [story, setStory] = useState(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    client.get("/stories/1").then(({ data }) => setStory(data.story)).catch(() => {});
  }, []);

  const media = detectMedia(story?.video?.asset_url);
  let embedSrc = "";
  if (media.type === "vimeo" && media.src) {
    // Always muted=1 here, never tied to the `muted` state — an iframe's
    // audio isn't controllable from the parent page without the
    // platform's JS SDK (which this component deliberately doesn't pull
    // in, matching the sibling Little Verses app's MicroOnboarding: it
    // only offers a custom mute control for self-hosted video). The
    // visitor unmutes using Vimeo's own on-embed volume control instead.
    const u = new URL(media.src);
    u.searchParams.set("autoplay", "1");
    u.searchParams.set("muted", "1");
    u.searchParams.set("background", "0");
    u.searchParams.set("title", "0");
    u.searchParams.set("byline", "0");
    u.searchParams.set("portrait", "0");
    u.searchParams.set("dnt", "1");
    embedSrc = u.href;
  } else if (media.type === "youtube" && media.src) {
    const u = new URL(media.src);
    u.searchParams.set("autoplay", "1");
    u.searchParams.set("mute", "1");
    embedSrc = u.href;
  } else if (media.type === "direct" && media.src) {
    embedSrc = media.src;
  }

  const title = story ? getLoc(story.title) : fallbackTitle;

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16" data-testid="free-story-showcase">
      <div className="max-w-2xl mx-auto text-center">
        <span className="hv-badge hv-badge-coral">A Little Peek</span>
        <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
          <span className="text-hv-navy">No sign-in.</span>{" "}
          <span className="text-hv-coral">Just press play.</span>
        </h2>
        <p className="mt-3 text-hv-slate leading-relaxed">
          Here&apos;s the first story, playing right now with the sound off. Turn it on when you&apos;re ready.
        </p>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="relative rounded-2xl overflow-hidden bg-hv-navy aspect-video shadow-xl">
            {embedSrc ? (
              media.type === "direct" ? (
                <video
                  key={embedSrc}
                  src={embedSrc}
                  className="w-full h-full"
                  autoPlay
                  muted={muted}
                  loop
                  playsInline
                  controls
                  data-testid="free-preview-video"
                />
              ) : (
                <iframe
                  key={embedSrc}
                  src={embedSrc}
                  title={title}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  data-testid="free-preview-iframe"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
                {story ? "Video coming soon" : "Loading…"}
              </div>
            )}
            {/* Only for self-hosted video (media.type === "direct") — this is
                a real in-place toggle of the <video> element's own muted
                property, not a reload. There's no equivalent for the
                Vimeo/YouTube embed above (see the comment on embedSrc). */}
            {media.type === "direct" && embedSrc && muted && (
              <button
                type="button"
                onClick={() => setMuted(false)}
                className="absolute top-4 start-4 inline-flex items-center gap-2 rounded-full bg-black/70 hover:bg-black/85 text-white text-sm font-bold px-4 py-2 transition"
                data-testid="free-preview-unmute"
              >
                <VolumeX size={16} /> Unmute
              </button>
            )}
          </div>
        </div>

        <div className="lg:col-span-5">
          <span className="hv-badge inline-flex items-center gap-1.5">
            <Sparkles size={12} className="text-hv-gold" /> Story #01 · {title}
          </span>
          <h3 className="hv-heading mt-4 text-2xl sm:text-3xl text-hv-navy">One tap. No account. No ads.</h3>
          <ol className="mt-5 space-y-4" data-testid="free-preview-steps">
            {[
              { t: "Watch the first story", d: "It is already playing on the left, with the sound off." },
              { t: "Turn on the sound", d: "Use the volume control in the video player when you are ready." },
              { t: "Open the full story", d: "Storybook, morals and quiz are free once you create an account." },
            ].map((s, i) => (
              <li key={s.t} className="flex items-start gap-3">
                <span className="shrink-0 grid place-items-center w-8 h-8 rounded-full bg-hv-coral text-white font-bold text-sm">{i + 1}</span>
                <span>
                  <span className="block font-bold text-hv-navy">{s.t}</span>
                  <span className="block text-hv-slate text-sm leading-relaxed">{s.d}</span>
                </span>
              </li>
            ))}
          </ol>
          <Link to="/story/1" data-testid="cta-open-full-story" className="hv-btn-primary mt-7 inline-flex items-center gap-2">
            <Play size={18} fill="white" strokeWidth={0} /> Open the full story
          </Link>
        </div>
      </div>
    </section>
  );
}
